
import { Component, OnInit, Optional } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DigitalLibraryService } from 'app/infrastructure/services/digitalLibrary.service';
import { AlertService, GrowlType } from 'app/infrastructure/services/alert.service';
import { FormulariosService, FormAttributeByType } from 'app/infrastructure/services/formularios.service';
import { SubscriptionsService } from 'app/infrastructure/services/subscriptions.service';
import { CountryTransform } from 'app/helpers/pipes/country.pipe';
import { ItemDigitalLibrary } from 'app/infrastructure/model/classes/digitalLibrary.class';
import { DigitalLibraryItemTypes } from 'app/infrastructure/model/enum/digitalLibraryItemTypes.enum';

interface Option {
    _id: any;
    _name: string;
    _selected?: boolean;
}

@Component({
    selector: 'app-digital-library-publish',
    templateUrl: './digital-library-publish.component.html'
})
export class DigitalLibraryPublishComponent implements OnInit {
    subscriptionCode: string;
    channelCode: string;
    productCode: string;
    scopeSpecificValue: any = [];

    formAttributes: FormAttributeByType[];

    attributes: Option[];
    selectedAttributes: Option[];

    systems: Option[];
    countries: Option[];

    itemLibrary: ItemDigitalLibrary;
    productType = DigitalLibraryItemTypes.Product.valueOf();
    templateType = DigitalLibraryItemTypes.Template.valueOf();

    organizations = [
        'Charles Taylor'
    ];

    types = [
        this.productType,
        this.templateType
    ];

    scope = [
        'Global',
        'Country',
        'Subscription'
    ];

    specificScope: Option[];
    specificScopeConfig = {
        displayKey: '_name',
        search: true,
        limitTo: 10
    };

    selectedScope = this.scope[0];

    constructor(
        private aRoute: ActivatedRoute,
        public libraryService: DigitalLibraryService,
        private alertService: AlertService,
        private router: Router,
        private formService: FormulariosService,
        private subscriptionsService: SubscriptionsService,
        private countryPipe: CountryTransform) { }

    ngOnInit() {
        this.subscriptionCode = this.aRoute.snapshot.params.codsistema;
        this.channelCode = this.aRoute.snapshot.params.codnegocio;
        this.productCode = this.aRoute.snapshot.params.codproducto;
        this.attributes = [];
        this.selectedAttributes = [];
        this.specificScope = [];

        this.initializeCountries();

        this.itemLibrary = {
            title: null,
            type: this.productType,
            extraInfo: {
                country: 'GBR',
                organization: this.organizations[0]
            },
            attributes: [],
            countries: [],
            systems: [],
        };

        this.subscriptionsService.getSubscriptions('A').subscribe(systems => {
            this.systems = systems.map(s => {
                const option: Option = {
                    _id: s.subscriptionCode,
                    _name: `(${s.subscriptionCode}) - ${s.descripcion}`
                };

                return option;
            });

            this.itemLibrary.extraInfo.subscription = this.subscriptionCode;
        });

        this.formService.getProductAttributesByService(this.subscriptionCode, this.channelCode, this.productCode).then(a => {
            this.formAttributes = a;
            this.attributes = this.formAttributes.map(formAttribute => {
                const attribute: Option = {
                    _id: `${formAttribute.formId}-${formAttribute.attributeCode}`,
                    _name: `(${formAttribute.serviceCode}) ${formAttribute.attributeCode}`,
                };

                return attribute;
            });
        });
    }

    save() {
        this.getAttributes();
        this.getSpecifictScope();

        if (this.validateScope()) {
            this.libraryService.publishProduct(this.subscriptionCode, this.channelCode, this.productCode, this.itemLibrary).subscribe(r => {
                this.alertService.showGrowl(
                    {
                        type: GrowlType.SUCCESS,
                        body: 'PUBLISH_PRODUCT_SUCCESS'
                    }
                );

                this.router.navigate(['../'], { relativeTo: this.aRoute });
            });
        }
    }

    isGlobal() {
        return this.selectedScope === this.scope[0];
    }

    isTemplate() {
        return this.itemLibrary.type === this.templateType;
    }

    onScopeSelect() {
        this.specificScope = [];

        switch (this.selectedScope) {
            case this.scope[1]:
                this.specificScope = this.countries;
                break;
            case this.scope[2]:
                this.specificScope = this.systems;
                break;
        }

        this.scopeSpecificValue = [];
    }

    // Assigns the selected objects to the value array so we can later clear it when we change scope.
    logSelected(event: any) {
        if (event.value !== undefined) {
            this.scopeSpecificValue = event.value;
        }
    }

    private getAttributes() {
        this.itemLibrary.attributes = [];

        if (!this.isTemplate()) {
            this.selectedAttributes.forEach(a => {
                const attribute = this.findAttribute(a);

                if (attribute) {
                    this.itemLibrary.attributes.push({
                        attributeCode: attribute.attributeCode,
                        formId: attribute.formId
                    });
                }
            });
        }
    }

    private getSpecifictScope() {
        const mappedScope = this.scopeSpecificValue.map(s => s._id);
        this.itemLibrary.systems = [];
        this.itemLibrary.countries = [];

        switch (this.selectedScope) {
            case this.scope[1]:
                this.itemLibrary.countries = mappedScope;
                break;
            case this.scope[2]:
                this.itemLibrary.systems = mappedScope;
                break;
        }
    }

    private findAttribute(selectedAttribute: Option) {
        const value = selectedAttribute._id.split('-');
        return this.formAttributes.find(a => a.formId === parseInt(value[0]) && a.attributeCode === value[1]);
    }

    private validateScope() {
        switch (this.selectedScope) {
            case this.scope[1]:
                if (this.itemLibrary.countries == null || this.itemLibrary.countries.length === 0) {
                    this.alertService.showGrowl(
                        {
                            type: GrowlType.DANGER,
                            body: 'INVALID_SCOPE_COUNTRY'
                        }
                    );

                    return false;
                }
                break;
            case this.scope[2]:
                if (this.itemLibrary.systems == null || this.itemLibrary.systems.length === 0) {
                    this.alertService.showGrowl(
                        {
                            type: GrowlType.DANGER,
                            body: 'INVALID_SCOPE_SYSTEM'
                        }
                    );

                    return false;
                }
                break;
        }

        return true;
    }

    private initializeCountries() {
        this.countries = [
            { _id: 'BRA', _name: this.countryPipe.transform('BRA') },
            { _id: 'ARG', _name: this.countryPipe.transform('ARG') },
            { _id: 'MEX', _name: this.countryPipe.transform('MEX') },
            { _id: 'GBR', _name: this.countryPipe.transform('GBR') }
        ];
    }
}



// WEBPACK FOOTER //
// D:/a/1/s/BackOffice/SOC/SxSoc.ClientWeb/src/app/private/digital-library/publish/digital-library-publish.component.ts